interface UploadParam {
  name: string;
  label?: string;
  widget?: string;
}

interface ReferenceCapability {
  key: string;
  ui_schema: { params: UploadParam[] };
}

export interface ReferenceEstimate {
  key: string;
  images: unknown[];
  error?: string;
}

/** Only the response for the current draft can satisfy a canonical reference. */
export function validatedReferenceMessage(
  capability: ReferenceCapability | null | undefined,
  params: Record<string, unknown>,
  key: string,
  estimate: ReferenceEstimate | null
): string | null {
  if (estimate?.key !== key) return missingReferenceMessage(capability, params);
  if (estimate.error) return estimate.error;
  return missingReferenceMessage(capability, { ...params, reference_images: estimate.images });
}

/**
 * Media a workflow cannot run without. The schema marks these fields optional
 * because the adapter accepts several combinations, so the requirement can only
 * be stated per capability.
 */
const ANY_OF: Record<string, string[]> = {
  reference_to_video: ["reference_images", "reference_videos", "reference_audios"],
};

const ALL_OF: Record<string, string[]> = {
  image_to_video: ["image"],
  first_last_frame: ["image", "end_image"],
  video_edit: ["source_video"],
  video_extend: ["source_video"],
};

function isFilled(value: unknown): boolean {
  return Array.isArray(value) ? value.length > 0 : Boolean(value);
}

function labelFor(params: UploadParam[], name: string): string {
  const schema = params.find((param) => param.name === name);
  if (schema?.label) return schema.label;
  return name.replace(/_/g, " ").replace(/^\w/, (c) => c.toUpperCase());
}

function declared(params: UploadParam[], names: string[]): string[] {
  return names.filter((name) =>
    params.some(
      (param) => param.name === name && (param.widget === "upload" || param.widget === "multi_upload")
    )
  );
}

function joinLabels(labels: string[], conjunction: string): string {
  if (labels.length <= 1) return labels[0] ?? "";
  return `${labels.slice(0, -1).join(", ")} ${conjunction} ${labels[labels.length - 1]}`;
}

/**
 * Why the selected workflow cannot generate yet, or `null` when it can. Only
 * capabilities this module knows are gated; an unknown one never blocks.
 */
export function missingReferenceMessage(
  capability: ReferenceCapability | null | undefined,
  params: Record<string, unknown>
): string | null {
  if (!capability) return null;
  const schema = capability.ui_schema?.params ?? [];

  const anyOf = declared(schema, ANY_OF[capability.key] ?? []);
  if (anyOf.length > 0 && !anyOf.some((name) => isFilled(params[name]))) {
    const labels = anyOf.map((name) => labelFor(schema, name).toLowerCase());
    return `Add at least one reference — ${joinLabels(labels, "or")} — before generating.`;
  }

  const missing = declared(schema, ALL_OF[capability.key] ?? []).filter(
    (name) => !isFilled(params[name])
  );
  if (missing.length > 0) {
    const labels = missing.map((name) => labelFor(schema, name));
    return missing.length === 1
      ? `${labels[0]} is required before generating.`
      : `${joinLabels(labels, "and")} are required before generating.`;
  }

  return null;
}
